import { Component, type ReactNode } from 'react';

import type { MapObjectInstance } from '../../content/schema/map';

interface Props {
  instance: MapObjectInstance;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Isolates the render of a single map object instance. On failure we swap
 * the live 3D prop for a red placeholder cube so one broken definition
 * can't take down the whole scene.
 */
export class MapObjectErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error) {
    console.error(
      `[map-editor] render failed for instance ${this.props.instance.id} (${this.props.instance.assetId}):`,
      error,
    );
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.instance !== this.props.instance && this.state.error) {
      // Reset after the instance has changed so the boundary can try again
      // (e.g. after the user fixes the param via the inspector).
      this.setState({ error: null });
    }
  }

  render(): ReactNode {
    if (this.state.error) {
      const position = this.props.instance.transform.position;
      return (
        <group position={position}>
          <mesh position={[0, 0.5, 0]} castShadow>
            <boxGeometry args={[0.7, 1, 0.7]} />
            <meshStandardMaterial color="#d13d3d" emissive="#5a0b0b" emissiveIntensity={0.35} />
          </mesh>
          <mesh position={[0, 1.2, 0]}>
            <boxGeometry args={[0.18, 0.18, 0.18]} />
            <meshStandardMaterial color="#ffee88" emissive="#ffdd44" emissiveIntensity={0.9} />
          </mesh>
        </group>
      );
    }
    return this.props.children;
  }
}
